import { env } from './env';

const base = (): string | null =>
  env.VITE_COMPLIANCE_URL?.replace(/\/+$/, '') || null;

/**
 * The legacy statuses whose companies can exist in compliance at all.
 *
 * Compliance mirrors MDS (its `sync_companies_from_mdm` walks `/api/v1/companies/` with no filter,
 * so every MDS company gets a row within its 5-minute schedule), and MDS is fed from legacy by an
 * importer with a **scope**: `mds/easybiz_sync/importers/scope.py` joins `companies` to
 * `incorporations` on the shared id and takes `incorporating`/`in_service` at
 * `incorporations.version >= 3`, plus `incorporating`/`in_service`/`incorporated` at version 1-2.
 * A `prospect` or `closed` company is excluded whatever its version, and a company with **no**
 * `incorporations` row is dropped by the join.
 *
 * So a link for a prospect is not a link that might fail — it is one that cannot work. Measured on
 * the legacy database (stage, which is a restore of prod, so this is one dataset and not two):
 * 1682 of 1949 companies are `prospect` or `closed`, and MDS holds **zero** of either — its 524
 * companies are 272 imported (`in_service` 185, `incorporating` 84, `incorporated` 3, by the
 * `legacy_status` the importer records) plus 252 that exist only in MDS.
 *
 * `onboarding` is in the DTO's enum and in no scope list; it is included here for completeness.
 */
const STATUSES_IN_COMPLIANCE = new Set([
  'incorporating',
  'in_service',
  'incorporated',
]);

/**
 * Whether this company can be in compliance, judged by the one signal the backoffice has.
 *
 * NOT the whole rule, and the gap is worth stating rather than hiding: the importer also needs an
 * `incorporations` row, which `AdminCompanyResponseDto` says nothing about. On the current data
 * that is one company — `in_service`, no onboarding record, a real client with seven members — so
 * the status check removes 1682 of the 1683 links that could not work, and the last one still
 * lands on compliance's own 404. Closing it needs a field from the backend saying whether the
 * company reached MDS; tracked separately.
 *
 * Fails closed: an undefined status yields no link, because a caller that does not know the status
 * cannot know the answer either.
 */
export const canBeInCompliance = (status?: string): boolean =>
  !!status && STATUSES_IN_COMPLIANCE.has(status);

/**
 * Deep links into the compliance service, which is where client data is edited from now on.
 *
 * **Why compliance and not MDS.** An earlier version of this file linked to master-data-service's
 * Django admin, on the reasoning that MDS owns the company record. It does own it, but nobody
 * edits it there: compliance has its own UI (a React app at the same host), and its API forwards
 * writes straight to MDS — `MasterDataProxyView` in `compliance/views.py` proxies PATCH/PUT/POST
 * to `<MDS>/api/v1/…` with the caller's own bearer token, and the app calls it through
 * `/api/v1/master-data/…`. So a correction made in compliance is a correction in MDS, one hop
 * shorter than a CSM going to MDS directly, and on the screen the compliance officers already use.
 *
 * **No role gate here, unlike the MDS link this replaces.** That gate existed because MDS grants
 * Django `is_staff` to the realm `admin` role only and bounces everyone else to an error page.
 * Compliance requires no role at all — `DEFAULT_PERMISSION_CLASSES` is plain `IsAuthenticated`
 * and its `OIDC_ADMIN_ROLE` check applies only to *its* Django admin, which is not what these
 * links point at — so every backoffice role that can open the tab can also open the link. Checked
 * against the deployed app, which does no role filtering of its own either.
 *
 * **Not every company is there.** The link is rendered only for a status whose companies reach
 * MDS, and therefore compliance — see `canBeInCompliance`. A prospect has no row there and the
 * deep link answered `404 {"detail":"No Company matches the given query."}`, which is what a CSM
 * saw before this check existed.
 *
 * **The id needs no translation.** `compliance.Company.id` is the MDM/MDS company UUID by
 * definition ("no separate local id"), and `easybiz_sync` creates each MDS company under the
 * legacy `companies.id` — measured on stage as 60/60 `in_service` and 85/86 `incorporating`
 * companies present under the same id. So the id the backoffice already holds is the id the URL
 * needs, in both places.
 */
export const complianceCompanyUrl = (
  companyId?: string,
  status?: string
): string | null => {
  const root = base();

  return root && companyId && canBeInCompliance(status)
    ? `${root}/companies/${companyId}`
    : null;
};

/**
 * The company's compliance case — the onboarding record.
 *
 * `/companies/:id/case` is the app's own route for it (it also serves `/case/:caseId`, but the
 * case id is not something the backoffice holds, and the plain route resolves the current one).
 * This is where the shareholders, the UBO answers and the KYC decisions live now that the
 * Onboarding tab is read-only.
 */
export const complianceCompanyCaseUrl = (
  companyId?: string,
  status?: string
): string | null => {
  const root = base();

  return root && companyId && canBeInCompliance(status)
    ? `${root}/companies/${companyId}/case`
    : null;
};
